import classNames from 'classnames'
import { cloneElement, forwardRef, HTMLAttributes, ReactElement } from 'react'

import { IconComponent } from '../icon/types'
import { ensurePropOf, Slot, Slottable } from '../utilities'
import styles from './rich-icon.module.scss'

export type RichIconElement = HTMLSpanElement

export const RichIconSizes = ['sm', 'md', 'lg'] as const
export type RichIconSize = (typeof RichIconSizes)[number]
export const RichIconTypes = [
  'primary',
  'positive',
  'warning',
  'negative',
  'discovery',
  'info',
] as const
export type RichIconType = (typeof RichIconTypes)[number]

export interface RichIconProps extends HTMLAttributes<RichIconElement> {
  icon: IconComponent
  size?: RichIconSize
  type?: RichIconType
  disabled?: boolean
  className?: string
  asChild?: boolean
}

export const RichIcon = forwardRef<RichIconElement, RichIconProps>(function RichIcon(
  props,
  forwardedRef
) {
  const {
    className,
    icon: Icon,
    size: sizeProp,
    type: typeProp,
    disabled = false,
    asChild,
    children,
    ...rest
  } = props

  const type = ensurePropOf<RichIconType>(RichIconTypes, typeProp, 'primary')
  const size = ensurePropOf<RichIconSize>(RichIconSizes, sizeProp, 'md')

  const classes = classNames(
    styles.root,
    {
      [styles.disabled]: disabled,
      [styles[size]]: size,
      [styles[type]]: type,
    },
    className
  )

  const Component = asChild ? Slot : 'span'

  return (
    <Component {...rest} className={classes} ref={forwardedRef}>
      {asChild ? (
        <Slottable>{cloneElement(children as ReactElement, {}, <Icon />)}</Slottable>
      ) : (
        <Icon />
      )}
    </Component>
  )
})
